export const isValidArray = (arr: TObject) =>
  arr && Array.isArray(arr) && arr.length;

export const isValidId = (id: TObject) => id && Number(id) > 0;

/**
 * Updates the `isExpended` property of the question at the specified index.
 * Sets the `isExpended` property to the provided value for the specified index,
 * and sets it to `false` for all other questions.
 *
 * @param {IFaqs[]} questions - The array of question objects.
 * @param {number} index - The index of the question to update.
 * @param {boolean} value - The boolean value to set for the `isExpended` property.
 * @returns {IFaqs[]} The updated array of question objects with the `isExpended` property updated.
 */
export const updateisExpended = (
  questions: IFaqs[],
  index: number,
  value: boolean
): IFaqs[] => {
  return questions.map((question, i) => ({
    ...question,
    isExpended: i === index ? value : false,
  }));
};

export const getLast4Digits = (cardNumber: string): string => {
  return cardNumber?.slice(-4);
};

/**
 * Adds a PDF ID to each item in the chat history array.
 * @param chatHistory - The original chat history array.
 * @param pdfId - The PDF ID to be added.
 * @returns A new chat history array with the PDF ID added to each item.
 */
export const addPdfIdToChatHistory = (
  chatHistory: any[],
  pdfId: string
): any[] => {
  return chatHistory.map((chat) => ({
    ...chat,
    pdf_id: pdfId,
  }));
};

/**
 * Filters chat history records by a specific PDF ID.
 * @param chatHistory - The chat history array.
 * @param pdfId - The PDF ID to filter by.
 * @returns A new array containing only the records with the specified PDF ID.
 */
export const filterChatHistoryByPdfId = (
  chatHistory: any[],
  pdfId: string
): any[] => {
  // Step 1: Filter the chat history based on the pdfId
  const filteredHistory = chatHistory.filter((chat) => chat?.pdf_id === pdfId);

  // Step 2: Remove pdf_id from each element in the filtered array
  const result = filteredHistory.map(({ pdf_id, ...rest }) => rest);

  return result;
};

export const carouselSliderProps = {
  superLargeDesktop: {
    // the naming can be any, depends on you.
    breakpoint: { max: 4000, min: 3000 },
    items: 5,
  },
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 3,
  },
  tablet: {
    breakpoint: { max: 1024, min: 464 },
    items: 2,
  },
  mobile: {
    breakpoint: { max: 464, min: 0 },
    items: 3,
  },
};

export function getYouTubeVideoId(url: string): string | null {
  if (url) {
    const regex =
      /(?:youtube\.com\/(?:.*v=|v\/|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;

    const match = url.match(regex);
    return match ? match[1] : null;
  }
  return null;
}

export function getDomainFromUrl(url: string): string | null {
  try {
    const parsedUrl = new URL(url);
    return parsedUrl?.hostname;
  } catch (error) {
    console.error("Invalid URL:", error);
    return null;
  }
}

export function tocArrayToMarkdown(toc: string) {
  let tocArray: string[] = [""]; // table of contents array
  let tocMarkdown: string = `> ### Table of content\n> \n`; // table of contents in markdown format
  try {
    tocArray = JSON.parse(toc);
    tocArray?.forEach((item, index) => {
      let slug = item
        .toLowerCase()
        .replace(/\s+/g, "-") // Replace all whitespace with hyphen
        .replace(/[^\p{L}\p{N}\-]+/gu, "");
      slug = `${index + 1}-${slug}`;
      tocMarkdown += `> [${index + 1}. ${item}](#${slug})\n`;
    });
    tocMarkdown += `\n`;
  } catch (err) {
    tocMarkdown = "";
  } finally {
    return tocMarkdown;
  }
}

export function detailedSummaryArrayToMarkdown(detailedSummary: string) {
  let detailedSummaryArray: any[] = [];
  let detailedSummaryMarkdown: string = "\n";
  try {
    detailedSummaryArray = JSON.parse(detailedSummary);
    detailedSummaryArray?.forEach((item, index) => {
      detailedSummaryMarkdown += `#### ${index + 1}. ${item.title}\n\n${
        item.summary
      }\n`;
    });
    detailedSummaryMarkdown += `\n`;
  } catch (err) {
    detailedSummaryMarkdown = "";
  } finally {
    return detailedSummaryMarkdown;
  }
}

export function handleSummary(summary: string) {
  let summaryData: string = "";
  try {
    summaryData = JSON.parse(summary);
  } catch (err) {
    summaryData = "";
  } finally {
    return summaryData;
  }
}

export function isValidUrl(url: string): boolean {
  try {
    // Attempt to create a new URL object
    new URL(url);
    return true;
  } catch (e) {
    // If an error is thrown, the string is not a valid URL
    return false;
  }
}

export const checkEmailEnding = (domains: any, email: any) => {
  return domains?.some((domain: any) => email?.endsWith(domain));
};

export const getFileExtension = (fileName: any, toUpperCase: any) => {
  const extension = fileName?.split(".").pop();
  return toUpperCase ? extension?.toUpperCase() : extension;
};

// Helper function for debounce
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export function generateSlug(name: string): string {
  return name
    ?.toLowerCase()
    ?.normalize("NFD")
    ?.replace(/[\u0300-\u036f]/g, "")
    ?.replace(/[^\p{L}\p{N}\s-]/gu, "")
    ?.replace(/\s+/g, "-")
    ?.replace(/-+/g, "-")
    ?.replace(/^-+|-+$/g, "");
}

export const getVideoDuration = (file: File): Promise<number> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";

    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      resolve(Math.floor(video.duration));
    };

    video.onerror = () => {
      window.URL.revokeObjectURL(video.src);
      reject(new Error("Failed to load video metadata"));
    };

    video.src = URL.createObjectURL(file);
  });
};

// Get YouTube video duration using YouTube Data API v3 (requires API key)
const getYouTubeDurationFromAPI = async (
  videoId: string,
  apiKey?: string
): Promise<number> => {
  if (!apiKey) {
    throw new Error("YouTube API key not provided");
  }

  try {
    const apiUrl = `https://www.googleapis.com/youtube/v3/videos?id=${videoId}&part=contentDetails&key=${apiKey}`;
    
    // Create AbortController for timeout (5 minutes)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, 300000); // 5 minute timeout

    try {
      const response = await fetch(apiUrl, {
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`YouTube API error: ${response.status}`);
      }

      const data = await response.json();
      if (data.items && data.items.length > 0) {
        const duration = data.items[0].contentDetails.duration;
        // Parse ISO 8601 duration (e.g., "PT1H2M10S")
        return parseISO8601Duration(duration);
      }
      throw new Error("Video not found");
    } catch (error: any) {
      clearTimeout(timeoutId);
      if (error.name === "AbortError") {
        throw new Error("YouTube API request timeout (5 minutes)");
      }
      throw error;
    }
  } catch (error) {
    throw error;
  }
};

// Parse ISO 8601 duration format (PT1H2M10S) to seconds
const parseISO8601Duration = (duration: string): number => {
  const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  if (!match) return 0;

  const hours = parseInt(match[1] || "0", 10);
  const minutes = parseInt(match[2] || "0", 10);
  const seconds = parseInt(match[3] || "0", 10);

  return hours * 3600 + minutes * 60 + seconds;
};

// Get YouTube duration by fetching video page and parsing metadata
const getYouTubeDurationFromPage = async (videoId: string): Promise<number> => {
  const videoUrl = `https://www.youtube.com/watch?v=${videoId}`;

  // Try multiple CORS proxy services
  const corsProxies = [
    `https://api.allorigins.win/get?url=${encodeURIComponent(videoUrl)}`,
    `https://cors-anywhere.herokuapp.com/${videoUrl}`,
    `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(videoUrl)}`,
  ];

  for (const proxyUrl of corsProxies) {
    try {
      // Create AbortController for timeout (5 minutes per proxy)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 300000); // 5 minute timeout

      let response;
      try {
        response = await fetch(proxyUrl, {
          method: "GET",
          headers: {
            Accept: "text/html",
          },
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
      } catch (fetchError: any) {
        clearTimeout(timeoutId);
        if (fetchError.name === "AbortError") {
          continue; // Try next proxy
        }
        throw fetchError;
      }

      if (response.ok) {
        let html: string;

        // Handle different proxy response formats
        if (proxyUrl.includes("allorigins.win")) {
          const data = await response.json();
          html = data.contents;
        } else {
          html = await response.text();
        }

        // Try multiple patterns to find duration
        const patterns = [
          /"lengthSeconds":"(\d+)"/,
          /"approxDurationMs":"(\d+)"/,
          /"duration":"PT(\d+H)?(\d+M)?(\d+S)?"/,
          /"videoDetails":\s*\{[^}]*"lengthSeconds":\s*"(\d+)"/,
        ];

        for (const pattern of patterns) {
          const match = html.match(pattern);
          if (match) {
            if (
              match[1] &&
              !match[1].includes("H") &&
              !match[1].includes("M") &&
              !match[1].includes("S")
            ) {
              // Direct seconds format
              const seconds = parseInt(match[1], 10);
              return seconds;
            } else if (
              (match[1] && match[1].includes("H")) ||
              match[2] ||
              match[3]
            ) {
              // ISO 8601 format
              const duration = parseISO8601Duration(
                `PT${match[1] || ""}${match[2] || ""}${match[3] || ""}`
              );
              return duration;
            } else if (match[1] && match[1].match(/^\d+$/)) {
              // Milliseconds format
              const seconds = Math.floor(parseInt(match[1], 10) / 1000);
              return seconds;
            }
          }
        }

        // Try to find in JSON-LD structured data
        const jsonLdMatch = html.match(
          /<script[^>]*type="application\/ld\+json"[^>]*>(.*?)<\/script>/s
        );
        if (jsonLdMatch) {
          try {
            const jsonLd = JSON.parse(jsonLdMatch[1]);
            if (jsonLd.duration) {
              const seconds = parseISO8601Duration(jsonLd.duration);
              return seconds;
            }
          } catch (e) {
            // JSON parse failed, continue
          }
        }
      }
    } catch (error) {
      // Try next proxy
      continue;
    }
  }

  throw new Error("Could not fetch YouTube duration from any proxy");
};

export const getVideoDurationFromUrl = (url: string): Promise<number> => {
  return new Promise((resolve, reject) => {
    if (!url || !url.trim()) {
      reject(new Error("URL is required"));
      return;
    }

    // Check if it's a YouTube URL
    const youtubeId = getYouTubeVideoId(url);
    if (youtubeId) {
      // Try to get YouTube duration
      // First, try with API key if available
      const apiKey = process.env.REACT_APP_YOUTUBE_API_KEY;

      if (apiKey) {
        // Use YouTube Data API v3
        getYouTubeDurationFromAPI(youtubeId, apiKey)
          .then((duration) => resolve(duration))
          .catch(() => {
            // If API fails, try page parsing
            getYouTubeDurationFromPage(youtubeId)
              .then((duration) => resolve(duration))
              .catch(() => resolve(0)); // Fallback to 0
          });
      } else {
        // No API key, try page parsing
        getYouTubeDurationFromPage(youtubeId)
          .then((duration) => resolve(duration))
          .catch(() => resolve(0)); // Fallback to 0 if all methods fail
      }
      return;
    }

    // For non-YouTube URLs, use video element
    const video = document.createElement("video");
    video.preload = "metadata";
    video.crossOrigin = "anonymous"; // Handle CORS if needed

    // Set a timeout to avoid hanging indefinitely
    const timeout = setTimeout(() => {
      reject(new Error("Timeout loading video metadata"));
    }, 10000); // 10 seconds timeout

    video.onloadedmetadata = () => {
      clearTimeout(timeout);
      resolve(Math.floor(video.duration));
    };

    video.onerror = () => {
      clearTimeout(timeout);
      // For some URLs, we might not be able to get duration due to CORS
      // Return 0 instead of rejecting - backend can handle it
      resolve(0);
    };

    video.src = url;
  });
};

export const formatDuration = (durationInSeconds: number): string => {
  // Handle edge cases
  if (!durationInSeconds || durationInSeconds < 0) {
    return "0:00";
  }

  const totalSeconds = Math.floor(durationInSeconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  // If duration is less than an hour, return MM:SS format
  if (hours === 0) {
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  }

  // If duration is an hour or more, return H:MM:SS format
  return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds
    .toString()
    .padStart(2, "0")}`;
};

export const getFileSize = (file: File): number => {
  return Math.floor(file.size / 1024);
};

// Formats KB size to appropriate unit (MB/GB) with unit suffix
export const formatFileSize = (sizeInKB: number): string => {
  // Handle edge cases
  if (!sizeInKB || sizeInKB < 0) {
    return "0 KB";
  }

  // If less than 1 MB (1024 KB), show in KB
  if (sizeInKB < 1024) {
    return `${sizeInKB.toFixed(1)} KB`;
  }

  // Convert to MB
  const sizeInMB = sizeInKB / 1024;

  // If less than 1 GB (1024 MB), show in MB
  if (sizeInMB < 1024) {
    return `${sizeInMB.toFixed(1)} MB`;
  }

  // Convert to GB
  const sizeInGB = sizeInMB / 1024;
  return `${sizeInGB.toFixed(1)} GB`;
};
